import React, { useEffect, useState, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { api, errText } from '@/lib/api';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Wallet, RefreshCw, RotateCw, CheckCircle2 } from 'lucide-react';
import { toast } from 'sonner';
import { StatCard, Banner, Loading, fmtDate } from '@/components/OpsUI';

export default function AppFinancePage() {
  const { t } = useTranslation();
  const [caura, setCaura] = useState(null);
  const [summary, setSummary] = useState(null);
  const [alerts, setAlerts] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [contribId, setContribId] = useState('');
  const [acting, setActing] = useState(false);

  const load = useCallback(async () => {
    setError(null);
    const safe = async (url, setter, params) => {
      try { const { data } = await api.get(url, { params }); setter(data); }
      catch (e) { if (url.includes('caura')) setError(errText(e)); }
    };
    await Promise.all([
      safe('/ops/webhooks/caura', setCaura, { limit: 30 }),
      safe('/ops/metrics/summary', setSummary),
      safe('/ops/alerts', setAlerts),
    ]);
    setLoading(false);
  }, []);

  useEffect(() => { load(); const id = setInterval(load, 45000); return () => clearInterval(id); }, [load]);

  const reprocess = async (deliveryId) => {
    setActing(true);
    try {
      const { data } = await api.post(`/ops/caura/webhooks/${deliveryId}/reprocess`);
      toast.success(`${t('ops_reprocess')}: ${data.status_before || '?'} → ${data.status_after || '?'}`);
      load();
    } catch (e) { toast.error(errText(e)); }
    finally { setActing(false); }
  };

  const reverify = async () => {
    if (!contribId.trim()) return;
    setActing(true);
    try {
      const { data } = await api.post(`/ops/contributions/${contribId.trim()}/reverify`);
      toast.success(`${t('ops_reverify')}: ${data.status_before || '?'} → ${data.status_after || '?'}`);
      setContribId('');
    } catch (e) { toast.error(errText(e)); }
    finally { setActing(false); }
  };

  const byStatus = summary?.contributions?.by_status || {};
  const backlog = alerts?.payment_backlog;

  return (
    <div className="space-y-6" data-testid="app-finance-page">
      <div className="flex items-end justify-between flex-wrap gap-3">
        <div>
          <h1 className="font-display text-2xl font-semibold flex items-center gap-2">
            <Wallet className="h-6 w-6 text-[hsl(var(--primary))]" /> {t('nav_app_finance')}
          </h1>
          <p className="text-sm text-[hsl(var(--muted-foreground))] mt-1">CauraPay · reconciliación de aportes</p>
        </div>
        <Button variant="outline" size="sm" onClick={load} data-testid="app-finance-refresh">
          <RefreshCw className="h-3.5 w-3.5 mr-1" /> {t('refresh')}
        </Button>
      </div>

      {loading ? <Loading label={t('loading')} /> : (
        <>
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4">
            <StatCard label="paid" value={byStatus.paid ?? 0} accent="text-emerald-400" />
            <StatCard label="pending" value={byStatus.pending ?? 0} accent="text-amber-400" />
            <StatCard label="verifying" value={byStatus.verifying ?? 0} accent="text-amber-400" />
            <StatCard label="expired" value={byStatus.expired ?? 0} accent="text-zinc-400" />
            <StatCard label={t('ops_payment_backlog')} value={backlog?.current ?? '—'} sub={`max ${backlog?.max_count ?? '—'}`} accent={backlog?.breaching ? 'text-red-400' : 'text-[hsl(var(--primary))]'} />
            <StatCard label="paid 24h (VES)" value={(summary?.contributions?.paid_24h_ves ?? 0).toLocaleString()} />
          </div>

          {/* Reverify by id */}
          <Card className="p-4 bg-card border border-border">
            <div className="text-sm font-display font-semibold mb-3 flex items-center gap-2">
              <CheckCircle2 className="h-4 w-4 text-[hsl(var(--primary))]" /> {t('ops_reverify')}
            </div>
            <div className="flex gap-2 max-w-xl">
              <Input value={contribId} onChange={(e) => setContribId(e.target.value)} placeholder={t('ops_contribution_id')} data-testid="ops-reverify-input" />
              <Button disabled={acting || !contribId.trim()} onClick={reverify} data-testid="ops-reverify-btn">{t('ops_reverify')}</Button>
            </div>
          </Card>

          {error && <Banner kind="error" message={String(error)} />}

          <Card className="bg-card border border-border">
            <div className="p-4 border-b border-border text-sm font-display font-semibold flex items-center justify-between">
              <span>{t('ops_caura_webhooks')}</span>
              {caura?.outcome_counts_7d && (
                <span className="text-xs font-mono text-[hsl(var(--muted-foreground))]">
                  7d: {Object.entries(caura.outcome_counts_7d).map(([k, v]) => `${k}:${v}`).join(' · ')}
                </span>
              )}
            </div>
            <div className="max-h-[420px] overflow-auto">
              <table className="w-full text-xs">
                <thead className="sticky top-0 bg-card">
                  <tr className="text-left text-[10px] uppercase tracking-wide text-[hsl(var(--muted-foreground))] border-b border-border">
                    <th className="px-3 py-2">event</th><th className="px-3 py-2">{t('ops_outcome')}</th><th className="px-3 py-2">church</th><th className="px-3 py-2">when</th><th className="px-3 py-2 text-right"></th>
                  </tr>
                </thead>
                <tbody className="font-mono">
                  {(caura?.recent || []).map((w) => (
                    <tr key={w.delivery_id} className="border-b border-border/40 hover:bg-accent/40">
                      <td className="px-3 py-2">{w.event}</td>
                      <td className="px-3 py-2">
                        <span className={w.outcome === 'processed' ? 'text-emerald-300' : w.outcome === 'unmapped' ? 'text-red-300' : 'text-amber-300'}>{w.outcome}</span>
                      </td>
                      <td className="px-3 py-2 truncate max-w-[160px]">{w.congregation_code || w.church_id || '—'}</td>
                      <td className="px-3 py-2 text-[hsl(var(--muted-foreground))]">{fmtDate(w.created_at)}</td>
                      <td className="px-3 py-2 text-right">
                        <Button variant="ghost" size="sm" disabled={acting} onClick={() => reprocess(w.delivery_id)} data-testid={`ops-reprocess-${w.delivery_id}`}>
                          <RotateCw className="h-3.5 w-3.5 mr-1" /> {t('ops_reprocess')}
                        </Button>
                      </td>
                    </tr>
                  ))}
                  {(!caura?.recent || caura.recent.length === 0) && (
                    <tr><td colSpan={5} className="px-4 py-8 text-center text-[hsl(var(--muted-foreground))] font-sans">{t('no_data')}</td></tr>
                  )}
                </tbody>
              </table>
            </div>
          </Card>
        </>
      )}
    </div>
  );
}
